// 管理者ダッシュボード(設計書 §3.5.2)

import { Link } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import { useAdminDashboard, type DashboardEvent, type EventSeverity } from '../../api/admin/dashboard';
import { AdminLayout } from '../../components/AdminLayout';
import { Card } from '../../components/ui/Card';

export function AdminDashboardPage() {
  const dashboard = useAdminDashboard();

  return (
    <AdminLayout title="ダッシュボード" description="システム全体の状況サマリです。30 秒ごとに更新します。">
      {dashboard.isLoading && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}
      {dashboard.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {dashboard.error instanceof ApiClientError
              ? dashboard.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}
      {dashboard.data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            <KpiCard
              label="配信中セッション"
              value={`${dashboard.data.active_sessions} / ${dashboard.data.max_sessions}`}
              accent={dashboard.data.active_sessions >= dashboard.data.max_sessions}
            />
            <KpiCard
              label="オンラインサーバー"
              value={`${dashboard.data.online_servers} / ${dashboard.data.total_servers}`}
              accent={dashboard.data.online_servers < dashboard.data.total_servers}
              link={{ to: '/admin/servers', label: 'サーバー管理' }}
            />
            <KpiCard
              label="登録パフォーマー"
              value={String(dashboard.data.performer_count)}
              link={{ to: '/admin/performers', label: 'パフォーマー一覧' }}
            />
            <KpiCard
              label="承認待ち"
              value={String(dashboard.data.pending_approval_count)}
              accent={dashboard.data.pending_approval_count > 0}
              link={{ to: '/admin/performers/pending', label: '承認画面へ' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <Card title="本日のエラー件数">
              <p
                style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: 500,
                  color:
                    dashboard.data.today_error_count > 0
                      ? 'var(--color-error-text)'
                      : 'var(--color-text-primary)',
                }}
              >
                {dashboard.data.today_error_count}
              </p>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: '11px',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                監査ログの severity=error / critical の件数
              </p>
            </Card>

            <Card title="最近のイベント">
              {dashboard.data.recent_events.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--color-text-tertiary)', fontSize: '11px' }}>
                  最近のイベントはありません。
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary)' }}>
                      <Th>時刻</Th>
                      <Th>重要度</Th>
                      <Th>アクター</Th>
                      <Th>アクション</Th>
                      <Th>メッセージ</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.data.recent_events.map((ev, i) => (
                      <EventRow key={i} event={ev} />
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  accent?: boolean;
  link?: { to: string; label: string };
}

function KpiCard({ label, value, accent, link }: KpiCardProps) {
  return (
    <div
      style={{
        background: 'var(--color-card)',
        border: 'var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: '10px',
          color: 'var(--color-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '22px',
          fontWeight: 500,
          color: accent ? 'var(--color-warning-text)' : 'var(--color-text-primary)',
          marginBottom: 6,
        }}
      >
        {value}
      </div>
      {link && (
        <Link
          to={link.to}
          style={{ fontSize: '11px', color: 'var(--color-text-accent)' }}
        >
          {link.label} →
        </Link>
      )}
    </div>
  );
}

function EventRow({ event }: { event: DashboardEvent }) {
  return (
    <tr style={{ borderTop: 'var(--border-default)' }}>
      <Td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>
        {formatDateTime(event.occurred_at)}
      </Td>
      <Td>
        <SeverityBadge severity={event.severity} />
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>{event.actor_label}</Td>
      <Td style={{ color: 'var(--color-text-primary)', fontFamily: 'monospace', fontSize: '10.5px' }}>
        {event.action}
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>{event.message}</Td>
    </tr>
  );
}

function SeverityBadge({ severity }: { severity: EventSeverity }) {
  const colors: Record<EventSeverity, { fg: string; bg: string }> = {
    info: { fg: 'var(--color-text-secondary)', bg: 'rgba(255,255,255,0.04)' },
    warning: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    error: { fg: 'var(--color-error-text)', bg: 'rgba(239,68,68,0.12)' },
    critical: { fg: 'white', bg: 'rgba(239,68,68,0.4)' },
  };
  const c = colors[severity];
  return (
    <span
      style={{
        fontSize: '9px',
        fontWeight: 500,
        color: c.fg,
        background: c.bg,
        padding: '2px 6px',
        borderRadius: '3px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {severity}
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        fontWeight: 500,
        padding: '8px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td style={{ padding: '8px', verticalAlign: 'top', ...style }}>{children}</td>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}
